import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useQueryClient } from "@tanstack/react-query"
import { usePortalAuth } from "@/hooks/usePortalAuth"
import { portalApi } from "@/lib/portal-api"
import { usePageTitle } from "@/hooks/usePageTitle"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Alert, AlertDescription } from "@/components/ui/alert"

export function PortalClientSelect() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data, isLoading } = usePortalAuth()
  const [selectingId, setSelectingId] = useState<string | null>(null)
  const [error, setError] = useState("")

  usePageTitle("Choose Vendor to View")

  useEffect(() => {
    if (!isLoading && data && data.accessible_clients.length <= 1) {
      navigate("/dashboard", { replace: true })
    }
  }, [data, isLoading, navigate])

  async function handleSelect(clientId: string) {
    setSelectingId(clientId)
    setError("")

    try {
      await portalApi("/api/portal/select-client", {
        method: "POST",
        body: JSON.stringify({ client_id: clientId }),
      })

      await queryClient.invalidateQueries({ queryKey: ["portal"] })
      navigate("/dashboard", { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to switch portal view")
    } finally {
      setSelectingId(null)
    }
  }

  if (isLoading) {
    return (
      <div className="bg-background flex min-h-screen items-center justify-center">
        <p className="text-muted-foreground">Loading portal...</p>
      </div>
    )
  }

  return (
    <div className="bg-background min-h-screen overflow-x-clip">
      <div className="mx-auto w-full max-w-3xl px-4 py-10">
        <Card className="min-w-0 overflow-hidden">
          <CardHeader>
            <CardTitle>Choose Vendor to View</CardTitle>
            <CardDescription>
              This email has access to multiple vendors or client workspaces. Select the one you
              want to view now.
            </CardDescription>
          </CardHeader>
          <CardContent className="min-w-0 space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {data?.accessible_clients.map((client) => {
              const isSelecting = selectingId === client.id

              return (
                <div
                  key={client.id}
                  className="flex max-w-full min-w-0 flex-col gap-3 overflow-hidden rounded-lg border p-4 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0 space-y-1">
                    <p className="text-muted-foreground text-sm [overflow-wrap:anywhere]">
                      {client.billing_entity_name || "Billing"}
                    </p>
                    <p className="text-lg font-semibold [overflow-wrap:anywhere]">{client.name}</p>
                    <p className="text-muted-foreground text-sm [overflow-wrap:anywhere]">
                      {client.billing_company ||
                        client.billing_name ||
                        client.billing_email ||
                        "No billing contact"}
                    </p>
                  </div>
                  <Button onClick={() => void handleSelect(client.id)} disabled={!!selectingId}>
                    {isSelecting ? "Opening..." : "Open Portal"}
                  </Button>
                </div>
              )
            })}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
